<script setup lang="ts">
import useViewTitle from '@panel/composables/useViewTitle';
import { GpTable, GpTableColumn, type GpTableDataRow } from '@gportal/uikit';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useImageStore } from 'gpcore-apikit/store/pinia/image';
import {
  AuthenticationType,
  type BasicImage,
  type OperatingSystem,
} from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { onMounted, ref } from 'vue';
import { toImageSrc } from 'gpcore-apikit/lib/fileHelper';
import useGpTableData from '@panel/composables/useGpTableData';
import EnumTag from 'gpcore-uikit/components/Component.EnumTag.vue';
import Status from 'gpcore-uikit/components/Component.Status.vue';
import ImagesLayout from '@panel/layouts/Layout.Images.vue';

interface PublicImageTableDataRow extends GpTableDataRow {
  icon: string;
  image: BasicImage;
}

useViewTitle('Public images');

const publicImages = ref<Array<OperatingSystem>>([]);
const isLoadingPublicImages = ref(false);

const imageStore = useImageStore();
const { addNotification } = useNotifications();
const { tableData: publicImagesTableData } = useGpTableData(publicImages, (data) => {
  const result: PublicImageTableDataRow[] = [];
  data.forEach((os: OperatingSystem) => {
    os.images.forEach((i: BasicImage) => {
      if (!i.available || !i.public) {
        return;
      }

      result.push({
        key: i.id,
        icon: toImageSrc(os.icon),
        image: i,
      });
    });
  });
  return result;
});

const reloadPublicImages = async () => {
  isLoadingPublicImages.value = true;
  try {
    const response = await imageStore.reloadPublicImages();
    publicImages.value = response.operatingSystems.map((os: OperatingSystem) => os);
  } catch (error) {
    addNotification({
      type: 'danger',
      message: (error as ServiceError).message,
    });
  } finally {
    isLoadingPublicImages.value = false;
  }
};

onMounted(() => {
  reloadPublicImages();
});
</script>

<template>
  <ImagesLayout>
    <template #content>
      <p>
        Public images are provided and maintained by GPCORE. These are the most common images, which
        are optimized to work with GPCORE. They are available for all users and projects. If you
        need a specific image or image version, which is not available, please contact
        <ExternalLink :url="$config.support.url">our support</ExternalLink>.
      </p>
      <h2 class="tw-mt-8">Public images</h2>
      <GpTable
        :data="publicImagesTableData"
        empty-message="No public images found."
        loading-message="Loading public images ..."
        class="tw-mt-4"
        :is-loading="isLoadingPublicImages"
      >
        <GpTableColumn field="icon" label="Image">
          <template
            #body="{
              data,
              row,
            }: {
              data: PublicImageTableDataRow['icon'];
              row: PublicImageTableDataRow;
            }"
          >
            <img
              v-if="data"
              :alt="`${row.image.name} icon`"
              class="tw-mb-1 tw-mr-4 tw-inline tw-h-6"
              :src="data"
            />
            {{ row.image.name }}
          </template>
        </GpTableColumn>
        <GpTableColumn field="version" label="Supported authentication types">
          <template #body="{ row }: { row: PublicImageTableDataRow }">
            <EnumTag
              v-for="(authType, index) in row.image.authenticationTypes"
              :key="index"
              :enum-map="AuthenticationType"
              :enum-value="authType"
              prefix="AUTHENTICATION_TYPE_"
              class="tw-mr-2"
            />
          </template>
        </GpTableColumn>
        <GpTableColumn field="spla-license" label="SPLA license included">
          <template #body="{ row }: { row: PublicImageTableDataRow }">
            <Status v-if="row.image.containsSplaLicense" state="success" label="Yes" />
            <Status v-else state="info" label="No" />
          </template>
        </GpTableColumn>
      </GpTable>
    </template>
  </ImagesLayout>
</template>
